import * as React from "react";
import { Check, ChevronDown, Lock, Pencil } from "lucide-react";
import { Controller, useFormContext } from "react-hook-form";
import {
  Button,
  Card,
  Checkbox,
  ChipGroup,
  FormField,
  Input,
  LogoUploader,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Select,
  Textarea,
} from "@chotca/ui";
import {
  BENEFITS_CATALOG,
  BENEFIT_LABEL_VI,
  DEFAULT_MIN_LABOR_AGE,
  EMPLOYMENT_TYPES,
  EMPLOYMENT_TYPE_LABEL_VI,
  POSITION_GROUPS,
  POSTING_DESCRIPTION_MAX,
  POSTING_TITLE_MAX,
  SKILLS_BY_POSITION,
  describeViolations,
  detectContactViolations,
  SKILL_LABEL_VI,
  VENUE_PRIORITY_GROUPS,
  WORKER_POSITION_LABEL_VI,
  type EmploymentType,
  type Gender,
  type ProvinceCode,
  type WorkerPosition,
} from "@chotca/config";
import { DEFAULTS, type FormInput, type PostingFormOutput } from "./schema";

// Shape the host app's AddressPicker must satisfy. Both web-employer and
// web-admin ship a compatible component; pass it through as a prop so this
// package stays free of routing/API concerns.
export interface AddressPickerValue {
  address: string;
  // 2025 post-merger ward (official new admin unit). Auto-filled by the host
  // picker via point-in-polygon, editable by the user.
  wardCode: string;
  wardName: string;
  wardProvinceCode: string;
  // Legacy admin fields — kept for the transition (display / NOT NULL columns).
  district: string;
  city: string;
  provinceCode: ProvinceCode | null;
  provinceName: string | null;
  lat: number | null;
  lng: number | null;
  mapUrl: string | null;
}
export interface AddressPickerProps {
  label?: string;
  value: AddressPickerValue;
  onChange: (v: AddressPickerValue) => void;
  disabled?: boolean;
}
export type AddressPickerComponent = React.ComponentType<AddressPickerProps>;
import { DESCRIPTION_SCAFFOLD, suggestTitle } from "./helpers";
import { SalaryRangeInput, Stepper } from "./fields";
import { usePostingFormMode } from "./mode";
import { usePreviewImageUpload } from "./preview-upload";

// Read-only pill for fields that are locked in edit mode (position /
// employment type are immutable after a posting is published).
function LockedFieldPill({ label }: { label: string }) {
  return (
    <div className="inline-flex items-center gap-2 rounded-full border border-smoke bg-mist px-3 py-1.5 text-sm font-semibold text-ink">
      <Lock size={13} strokeWidth={2.25} className="text-ash" />
      {label}
    </div>
  );
}

type Ctx = ReturnType<
  typeof useFormContext<FormInput, unknown, PostingFormOutput>
>;

function useCtx(): Ctx {
  return useFormContext<FormInput, unknown, PostingFormOutput>();
}

const TOP_BENEFITS: ReadonlyArray<(typeof BENEFITS_CATALOG)[number]> = [
  "MEAL",
  "TIP_POOLED",
  "BONUS_MONTHLY",
  "UNIFORM_PROVIDED",
  "TRAINING_PROVIDED",
  "FLEXIBLE_SHIFT",
];

const GENDER_ANY = "ANY";
const GENDER_OPTIONS = [
  { value: GENDER_ANY, label: "Cả hai (mặc định)" },
  { value: "MALE", label: "Nam" },
  { value: "FEMALE", label: "Nữ" },
];

// ── Section header helper ──────────────────────────────────────────────────
function SectionHeader({
  step,
  title,
  hint,
}: {
  step: number;
  title: string;
  hint?: string;
}) {
  return (
    <div className="mb-4">
      <h3 className="flex items-center gap-2 text-base font-extrabold text-ink">
        <span className="grid h-6 w-6 place-items-center rounded-full bg-ink text-[11px] font-bold text-paper">
          {step}
        </span>
        {title}
      </h3>
      {hint ? <p className="ml-8 mt-1 text-xs text-ink/60">{hint}</p> : null}
    </div>
  );
}

// ── Template Hero ──────────────────────────────────────────────────────────
export interface TemplatePickerItem {
  id: string;
  name: string;
  label?: string | null;
  salaryMin: number;
  salaryMax: number;
  salaryType: string;
  defaultHeadcount: number;
}

/** A user's pick on the mobile chooser screen, committed when they tap "Tiếp". */
export type ChooserSelection =
  | { type: "system"; id: string }
  | { type: "saved"; id: string }
  | { type: "blank" };

export interface TemplateChooserScreenProps {
  systemTemplates?: TemplatePickerItem[];
  savedTemplates?: TemplatePickerItem[];
  selected: ChooserSelection | null;
  onSelect: (selection: ChooserSelection | null) => void;
  manageHref?: string;
  onManageTemplates?: () => void;
}

/**
 * Mobile-only full-screen template picker shown *before* the step wizard.
 * Tapping a card highlights it (selection is committed on the footer's "Tiếp").
 */
export function TemplateChooserScreen({
  systemTemplates,
  savedTemplates,
  selected,
  onSelect,
  manageHref,
  onManageTemplates,
}: TemplateChooserScreenProps) {
  const isSelected = (candidate: ChooserSelection) =>
    selected?.type === candidate.type &&
    (candidate.type === "blank" ||
      ("id" in selected && selected.id === candidate.id));

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-extrabold text-ink">
            Bắt đầu nhanh (tuỳ chọn)
          </h2>
          <p className="mt-0.5 text-xs text-ink/60">
            Chọn 1 mẫu để fill sẵn ~80% form, hoặc bấm "Tiếp" để tự nhập từ đầu.
          </p>
        </div>
        {manageHref ? (
          <a
            href={manageHref}
            onClick={(e) => {
              if (onManageTemplates) {
                e.preventDefault();
                onManageTemplates();
              }
            }}
            className="shrink-0 whitespace-nowrap text-xs font-semibold text-ink/70 underline-offset-4 hover:underline"
          >
            Quản lý mẫu
          </a>
        ) : null}
      </div>

      {savedTemplates && savedTemplates.length > 0 ? (
        <section>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink/55">
            Mẫu đã lưu của bạn
          </p>
          <div className="space-y-3">
            {savedTemplates.map((tpl) => {
              const sel = isSelected({ type: "saved", id: tpl.id });
              return (
                <ChooserCard
                  key={tpl.id}
                  tpl={tpl}
                  selected={sel}
                  onClick={() =>
                    onSelect(sel ? null : { type: "saved", id: tpl.id })
                  }
                />
              );
            })}
          </div>
        </section>
      ) : null}

      {systemTemplates && systemTemplates.length > 0 ? (
        <section>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink/55">
            Mẫu hệ thống
          </p>
          <div className="space-y-3">
            {systemTemplates.map((tpl) => {
              const sel = isSelected({ type: "system", id: tpl.id });
              return (
                <ChooserCard
                  key={tpl.id}
                  tpl={tpl}
                  selected={sel}
                  onClick={() =>
                    onSelect(sel ? null : { type: "system", id: tpl.id })
                  }
                />
              );
            })}
          </div>
        </section>
      ) : null}
    </div>
  );
}

function ChooserCard({
  tpl,
  selected,
  onClick,
}: {
  tpl: TemplatePickerItem;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={`flex w-full touch-manipulation items-center justify-between gap-3 rounded-xl border-2 p-4 text-left transition-colors ${
        selected
          ? "border-lime bg-lime/15"
          : "border-smoke bg-paper hover:border-ink/40 hover:bg-mist/50"
      }`}
    >
      <span className="min-w-0">
        <span className="block truncate text-sm font-bold text-ink">
          {tpl.label ?? tpl.name}
        </span>
        <span className="mt-0.5 block truncate text-xs text-ink/60">
          {formatRange(tpl.salaryMin, tpl.salaryMax, tpl.salaryType)} ·{" "}
          {tpl.defaultHeadcount} người
        </span>
      </span>
      <span
        className={`grid h-5 w-5 shrink-0 place-items-center rounded-full border-2 transition-colors ${
          selected
            ? "border-lime bg-lime text-ink"
            : "border-smoke text-transparent"
        }`}
      >
        <Check size={12} strokeWidth={3} />
      </span>
    </button>
  );
}

export interface TemplateBarProps {
  appliedSystemId: string | null;
  appliedSavedId?: string | null;
  systemTemplates?: TemplatePickerItem[];
  savedTemplates?: TemplatePickerItem[];
  onApplySystem: (id: string) => void;
  onApplySaved?: (id: string) => void;
  onClear: () => void;
  manageHref?: string;
  onManageTemplates?: () => void;
}

/**
 * Desktop-only slim bar shown at the top of the (single-scroll) form. Replaces
 * the old large hero card: a popover lists templates, an applied chip lets the
 * user clear the applied template.
 */
export function TemplateBar({
  appliedSystemId,
  appliedSavedId,
  systemTemplates,
  savedTemplates,
  onApplySystem,
  onApplySaved,
  onClear,
  manageHref,
  onManageTemplates,
}: TemplateBarProps) {
  const [open, setOpen] = React.useState(false);
  const appliedSystem = appliedSystemId
    ? (systemTemplates?.find((t) => t.id === appliedSystemId) ?? null)
    : null;
  const appliedSaved = appliedSavedId
    ? (savedTemplates?.find((t) => t.id === appliedSavedId) ?? null)
    : null;
  const applied = appliedSystem ?? appliedSaved;

  if (applied) {
    return (
      <div className="flex flex-wrap items-center gap-3 rounded-xl border-2 border-lime bg-lime/15 px-4 py-3">
        <span className="text-xs font-bold uppercase tracking-wide text-ink/70">
          Đã áp dụng mẫu
        </span>
        <span className="min-w-0 truncate text-sm font-extrabold text-ink">
          {appliedSystem?.label ?? applied.name}
        </span>
        <span className="flex-1" />
        {manageHref ? (
          <a
            href={manageHref}
            onClick={(e) => {
              if (onManageTemplates) {
                e.preventDefault();
                onManageTemplates();
              }
            }}
            className="text-xs font-semibold text-ink/70 underline-offset-4 hover:underline"
          >
            Quản lý mẫu
          </a>
        ) : null}
        <Button type="button" variant="ghost" size="sm" onClick={onClear}>
          Bỏ mẫu
        </Button>
      </div>
    );
  }

  const hasTemplates =
    (savedTemplates?.length ?? 0) + (systemTemplates?.length ?? 0) > 0;

  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-smoke bg-paper px-4 py-3">
      <div className="min-w-0">
        <p className="text-sm font-bold text-ink">Bắt đầu nhanh</p>
        <p className="mt-0.5 text-xs text-ink/60">
          Chọn 1 mẫu để fill sẵn ~80% form, hoặc tự nhập từ đầu.
        </p>
      </div>
      <span className="flex-1" />
      {manageHref ? (
        <a
          href={manageHref}
          onClick={(e) => {
            if (onManageTemplates) {
              e.preventDefault();
              onManageTemplates();
            }
          }}
          className="text-xs font-semibold text-ink/70 underline-offset-4 hover:underline"
        >
          Quản lý mẫu
        </a>
      ) : null}
      {hasTemplates ? (
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="secondary" size="md">
              Chọn mẫu nhanh
              <ChevronDown size={16} className="ml-1" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            className="max-h-[60vh] w-80 overflow-y-auto p-2"
          >
            {savedTemplates && savedTemplates.length > 0 ? (
              <>
                <p className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-ink/55">
                  Mẫu đã lưu
                </p>
                {savedTemplates.map((tpl) => (
                  <TemplateRow
                    key={tpl.id}
                    tpl={tpl}
                    onClick={() => {
                      onApplySaved?.(tpl.id);
                      setOpen(false);
                    }}
                  />
                ))}
              </>
            ) : null}
            {systemTemplates && systemTemplates.length > 0 ? (
              <>
                <p className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wide text-ink/55">
                  Mẫu hệ thống
                </p>
                {systemTemplates.map((tpl) => (
                  <TemplateRow
                    key={tpl.id}
                    tpl={tpl}
                    onClick={() => {
                      onApplySystem(tpl.id);
                      setOpen(false);
                    }}
                  />
                ))}
              </>
            ) : null}
          </PopoverContent>
        </Popover>
      ) : null}
    </div>
  );
}

function TemplateRow({
  tpl,
  onClick,
}: {
  tpl: TemplatePickerItem;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="block w-full rounded-lg px-2 py-2 text-left transition-colors hover:bg-mist"
    >
      <span className="block truncate text-sm font-bold text-ink">
        {tpl.label ?? tpl.name}
      </span>
      <span className="mt-0.5 block truncate text-xs text-ink/60">
        {formatRange(tpl.salaryMin, tpl.salaryMax, tpl.salaryType)} ·{" "}
        {tpl.defaultHeadcount} người
      </span>
    </button>
  );
}

function formatRange(min: number, max: number, type: string): string {
  const fmt = (n: number) =>
    n >= 1_000_000
      ? `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}tr`
      : `${n / 1000}k`;
  const unit =
    type === "PER_MONTH"
      ? "/tháng"
      : type === "PER_DAY"
        ? "/ngày"
        : type === "PER_SHIFT"
          ? "/ca"
          : "/giờ";
  return `${fmt(min)}–${fmt(max)}${unit}`;
}

// ── Section 1 — Position (venue + position + skills inline) ────────────────
export function PositionSection() {
  const { control, watch, setValue, formState } = useCtx();
  const { lockPosition } = usePostingFormMode();
  const venueType = watch("venueType");
  const position = watch("position");
  const [showAllGroups, setShowAllGroups] = React.useState(false);

  const orderedGroups = React.useMemo(() => {
    const order = VENUE_PRIORITY_GROUPS[venueType] ?? [];
    return [...POSITION_GROUPS].sort((a, b) => {
      const ai = order.indexOf(a.key) === -1 ? 999 : order.indexOf(a.key);
      const bi = order.indexOf(b.key) === -1 ? 999 : order.indexOf(b.key);
      return ai - bi;
    });
  }, [venueType]);

  const visibleGroups = showAllGroups
    ? orderedGroups
    : orderedGroups.slice(0, 1);
  const hiddenCount = orderedGroups.length - 1;
  const positionSkills = SKILLS_BY_POSITION[position] ?? [];

  return (
    <Card className="p-6">
      <SectionHeader step={1} title="Vị trí cần tuyển" />

      <div className="space-y-4">
        <div id="field-position">
          <FormField
            label="Vị trí"
            error={formState.errors.position?.message}
            description={
              lockPosition
                ? "Không thể đổi vị trí sau khi đăng tin — tạo tin mới nếu cần tuyển vị trí khác."
                : undefined
            }
          >
            {lockPosition ? (
              <LockedFieldPill
                label={
                  WORKER_POSITION_LABEL_VI[position as WorkerPosition] ??
                  position
                }
              />
            ) : (
              <Controller
                control={control}
                name="position"
                render={({ field }) => (
                  <div className="space-y-3">
                    {visibleGroups.map((g) => (
                      <fieldset
                        key={g.key}
                        className="rounded-lg border border-smoke p-3"
                      >
                        <legend className="px-1 text-xs font-semibold uppercase tracking-wide text-ink/60">
                          {g.label}
                        </legend>
                        <ChipGroup<WorkerPosition>
                          mode="single"
                          tone="lime"
                          value={field.value}
                          onChange={(next) => {
                            field.onChange(next);
                            setValue("skillsRequired", [], {
                              shouldDirty: true,
                            });
                            setValue("skillsPreferred", [], {
                              shouldDirty: true,
                            });
                          }}
                          options={g.positions.map((p) => ({
                            value: p as WorkerPosition,
                            label:
                              WORKER_POSITION_LABEL_VI[p as WorkerPosition],
                          }))}
                        />
                      </fieldset>
                    ))}

                    {!showAllGroups && hiddenCount > 0 ? (
                      <button
                        type="button"
                        onClick={() => setShowAllGroups(true)}
                        className="text-xs font-semibold text-ink/70 underline-offset-4 hover:underline"
                      >
                        Xem thêm {hiddenCount} nhóm vị trí khác →
                      </button>
                    ) : null}
                  </div>
                )}
              />
            )}
          </FormField>
        </div>

        {positionSkills.length > 0 ? (
          <FormField
            label="Kỹ năng yêu cầu"
            description='Tap để chọn — giúp app match đúng ứng viên. Yêu cầu chi tiết hơn (kinh nghiệm, chứng chỉ…) ghi thêm ở phần "Mô tả công việc" bên dưới.'
          >
            <Controller
              control={control}
              name="skillsRequired"
              render={({ field }) => (
                <ChipGroup<string>
                  mode="multi"
                  tone="lime"
                  value={field.value ?? []}
                  onChange={field.onChange}
                  options={positionSkills.map((s) => ({
                    value: s,
                    label: SKILL_LABEL_VI[s] ?? s,
                  }))}
                />
              )}
            />
          </FormField>
        ) : null}
      </div>
    </Card>
  );
}

// Optional square preview image shown at the top of the Content section. Only
// renders when the host injects an uploader (employer + admin both do). The
// image becomes the feed-card thumbnail; when absent the feed falls back to the
// org logo → gallery → default.
function PreviewImageField() {
  const upload = usePreviewImageUpload();
  const { watch, setValue } = useCtx();
  if (!upload) return null;
  const url = watch("previewImageUrl") ?? null;
  return (
    <div id="field-previewImageKey">
      <FormField
        label="Ảnh preview"
        description="Tuỳ chọn. Ảnh vuông hiển thị trên feed (ảnh quán, món, không gian). Để trống sẽ tự dùng ảnh đại diện quán. JPG/PNG/WebP."
      >
        <LogoUploader
          url={url}
          onUpload={async (file, onProgress) => {
            const r = await upload.onUpload(file, onProgress);
            return { key: r.key, url: r.url };
          }}
          onChange={(next) => {
            setValue("previewImageKey", next.key, { shouldDirty: true });
            setValue("previewImageUrl", next.url, { shouldDirty: true });
          }}
          onRemove={() => {
            setValue("previewImageKey", null, { shouldDirty: true });
            setValue("previewImageUrl", null, { shouldDirty: true });
          }}
          labels={{
            upload: "Tải ảnh lên",
            change: "Đổi ảnh",
            remove: "Xoá ảnh",
            uploadError: "Tải ảnh thất bại, thử lại",
            view: "Xem ảnh",
            close: "Đóng",
          }}
        />
      </FormField>
    </div>
  );
}

// ── Section 2 — Content (title, employment, salary, headcount, description)
export function ContentSection() {
  const { control, register, watch, setValue, formState } = useCtx();
  const { lockEmploymentType, minHeadcount } = usePostingFormMode();
  const errors = formState.errors;
  const titleValue = watch("title") ?? "";
  const descriptionValue = watch("description") ?? "";
  const titleViolations = React.useMemo(
    () => detectContactViolations(titleValue),
    [titleValue],
  );
  const descViolations = React.useMemo(
    () => detectContactViolations(descriptionValue),
    [descriptionValue],
  );

  const handleSuggestTitle = () => {
    const next = suggestTitle({
      position: watch("position"),
      venueType: watch("venueType"),
      employmentType: watch("employmentType"),
    });
    setValue("title", next, { shouldDirty: true, shouldValidate: true });
  };

  const handleInsertScaffold = () => {
    setValue("description", DESCRIPTION_SCAFFOLD, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  return (
    <Card className="p-6">
      <SectionHeader step={2} title="Nội dung tin" />

      <div className="space-y-5">
        <PreviewImageField />

        <div id="field-employmentType">
          <FormField
            label="Loại hình"
            error={errors.employmentType?.message}
            description={
              lockEmploymentType
                ? "Không thể đổi loại hình sau khi đăng tin."
                : undefined
            }
          >
            {lockEmploymentType ? (
              <LockedFieldPill
                label={
                  EMPLOYMENT_TYPE_LABEL_VI[
                    watch("employmentType") as EmploymentType
                  ] ?? watch("employmentType")
                }
              />
            ) : (
              <Controller
                control={control}
                name="employmentType"
                render={({ field }) => (
                  <ChipGroup<EmploymentType>
                    mode="single"
                    tone="lime"
                    value={field.value}
                    onChange={field.onChange}
                    options={EMPLOYMENT_TYPES.map((p) => ({
                      value: p as EmploymentType,
                      label: EMPLOYMENT_TYPE_LABEL_VI[p as EmploymentType],
                    }))}
                  />
                )}
              />
            )}
          </FormField>
        </div>

        <div id="field-title">
          <FormField
            label="Tiêu đề"
            error={errors.title?.message}
            actions={
              <button
                type="button"
                onClick={handleSuggestTitle}
                className="text-xs font-semibold text-ink/70 underline-offset-4 hover:underline"
              >
                Gợi ý tự động
              </button>
            }
          >
            <Input
              {...register("title")}
              placeholder="VD: Tuyển phục vụ ca tối"
              invalid={!!errors.title}
              maxLength={POSTING_TITLE_MAX}
            />
            <div className="mt-1 flex justify-between gap-3 text-[11px] text-ink/55">
              <span>
                {titleValue.length}/{POSTING_TITLE_MAX} ký tự
              </span>
            </div>
            {titleViolations.length > 0 ? (
              <p className="mt-1 rounded-md border border-danger/30 bg-danger/10 px-2.5 py-1.5 text-xs text-danger">
                ⚠ Phát hiện thông tin liên lạc:{" "}
                {describeViolations(titleViolations)}. Đăng kèm liên lạc sẽ{" "}
                <strong>khoá tài khoản vĩnh viễn</strong>.
              </p>
            ) : null}
          </FormField>
        </div>

        <div id="field-salaryMin">
          <Controller
            control={control}
            name="salaryMin"
            render={({ field: minField }) => (
              <Controller
                control={control}
                name="salaryMax"
                render={({ field: maxField }) => (
                  <Controller
                    control={control}
                    name="salaryType"
                    render={({ field: typeField }) => (
                      <SalaryRangeInput
                        min={Number(minField.value) || 0}
                        max={Number(maxField.value) || 0}
                        type={typeField.value}
                        onMinChange={(n) => minField.onChange(n)}
                        onMaxChange={(n) => maxField.onChange(n)}
                        onTypeChange={(t) => typeField.onChange(t)}
                        errorMin={errors.salaryMin?.message}
                        errorMax={errors.salaryMax?.message}
                      />
                    )}
                  />
                )}
              />
            )}
          />
        </div>

        <div id="field-headcount">
          <FormField
            label="Số người cần tuyển"
            error={errors.headcount?.message}
            description={
              minHeadcount && minHeadcount > 1
                ? `Đã có ${minHeadcount} ứng viên được nhận — không thể hạ xuống dưới ${minHeadcount}.`
                : undefined
            }
          >
            <Controller
              control={control}
              name="headcount"
              render={({ field }) => (
                <Stepper
                  ariaLabel="Số người cần tuyển"
                  value={Number(field.value) || 1}
                  onChange={(n) => field.onChange(n)}
                  min={Math.max(1, minHeadcount ?? 1)}
                  max={50}
                />
              )}
            />
          </FormField>
        </div>

        <div className="rounded-2xl border border-smoke/60 bg-smoke/30 px-4 py-3 text-xs text-ink/70">
          <span className="font-semibold text-ink/85">Hết hạn sau 7 ngày</span>{" "}
          — tin sẽ tự động ẩn khỏi danh sách của ứng viên sau 7 ngày kể từ lúc
          đăng.
        </div>

        <div id="field-description">
          <FormField
            label="Mô tả công việc"
            error={errors.description?.message}
            actions={
              <button
                type="button"
                onClick={handleInsertScaffold}
                className="text-xs font-semibold text-ink/70 underline-offset-4 hover:underline"
              >
                Chèn mẫu
              </button>
            }
          >
            <Textarea
              {...register("description")}
              rows={6}
              placeholder="Mô tả công việc, thời gian cụ thể, yêu cầu thêm..."
              maxLength={POSTING_DESCRIPTION_MAX}
            />
            <div className="mt-1 flex justify-between gap-3 text-[11px] text-ink/55">
              <span>
                {descriptionValue.length}/{POSTING_DESCRIPTION_MAX} ký tự
              </span>
            </div>
            {descViolations.length > 0 ? (
              <p className="mt-1 rounded-md border border-danger/30 bg-danger/10 px-2.5 py-1.5 text-xs text-danger">
                ⚠ Phát hiện thông tin liên lạc:{" "}
                {describeViolations(descViolations)}. Đăng kèm liên lạc sẽ{" "}
                <strong>khoá tài khoản vĩnh viễn</strong>.
              </p>
            ) : null}
          </FormField>
        </div>
      </div>
    </Card>
  );
}

// ── Section 3 — Benefits ───────────────────────────────────────────────────
export function BenefitsSection() {
  const { control, watch } = useCtx();
  const selected = watch("benefits") ?? [];
  const hasSelectedHidden = selected.some(
    (b) => !TOP_BENEFITS.includes(b as (typeof TOP_BENEFITS)[number]),
  );
  const [showAll, setShowAll] = React.useState(hasSelectedHidden);

  const visible = showAll ? BENEFITS_CATALOG : TOP_BENEFITS;
  const hiddenCount = BENEFITS_CATALOG.length - TOP_BENEFITS.length;

  return (
    <Card className="p-6">
      <SectionHeader
        step={3}
        title="Quyền lợi"
        hint="Chọn các quyền lợi có ở quán."
      />

      <Controller
        control={control}
        name="benefits"
        render={({ field }) => (
          <div className="space-y-3">
            <ChipGroup<string>
              mode="multi"
              tone="lime"
              value={field.value ?? []}
              onChange={field.onChange}
              options={visible.map((b) => ({
                value: b,
                label: BENEFIT_LABEL_VI[b as (typeof BENEFITS_CATALOG)[number]],
              }))}
            />
            {!showAll && hiddenCount > 0 ? (
              <button
                type="button"
                onClick={() => setShowAll(true)}
                className="text-xs font-semibold text-ink/70 underline-offset-4 hover:underline"
              >
                Xem thêm {hiddenCount} quyền lợi khác →
              </button>
            ) : null}
          </div>
        )}
      />
    </Card>
  );
}

// ── Section 4 — Location (multi-address picker + inline CRUD) ──────────────
export interface OrgAddressOption {
  id: string;
  alias: string;
  address: string;
  // Nullable on legacy rows (pre-migration); new rows always carry them.
  wardCode: string | null;
  wardName: string | null;
  wardProvinceCode: string | null;
  district: string;
  city: string;
  provinceCode: ProvinceCode | null;
  lat: number;
  lng: number;
  mapUrl?: string | null;
  archivedAt?: string | null;
  inUseByOpenPostings: number;
}

// Payload the branch editor emits — ward is always present (the editor gates on
// it), so it's non-null here unlike the display-shaped OrgAddressOption above.
export interface OrgAddressInput {
  alias: string;
  address: string;
  wardCode: string;
  wardName: string;
  wardProvinceCode: string;
  district: string;
  city: string;
  provinceCode: ProvinceCode | null;
  lat: number;
  lng: number;
  mapUrl?: string | null;
}

export interface LocationSectionProps {
  addresses: OrgAddressOption[];
  loading?: boolean;
  // Inline CRUD callbacks. Each resolves with the saved/archived address so
  // the form can reflect the change immediately. Failures bubble up — the
  // caller wires toasts.
  onCreate: (input: OrgAddressInput) => Promise<OrgAddressOption>;
  onUpdate: (
    id: string,
    input: Partial<OrgAddressInput>,
  ) => Promise<OrgAddressOption>;
  onArchive: (id: string) => Promise<void>;
  /** Host-provided component used inside the inline create/edit dialog. */
  AddressPicker: AddressPickerComponent;
}

export function LocationSection(props: LocationSectionProps) {
  const { addresses, loading, onCreate, onUpdate, onArchive, AddressPicker } =
    props;
  const { lockedAddressIds, addressInterviewCounts } = usePostingFormMode();
  const lockedSet = React.useMemo(
    () => new Set(lockedAddressIds ?? []),
    [lockedAddressIds],
  );
  const { control, watch, setValue } = useCtx();
  const selected = watch("addressIds") ?? [];

  // Only non-archived addresses participate in the picker. Archived rows
  // remain selectable if they were already attached (won't happen for new
  // postings, but defensive for edit flows).
  const visible = React.useMemo(
    () => addresses.filter((a) => !a.archivedAt || selected.includes(a.id)),
    [addresses, selected],
  );

  // Auto-seed the form with the org's default address (first non-archived)
  // once on mount when the form is still empty.
  const seeded = React.useRef(false);
  React.useEffect(() => {
    if (seeded.current) return;
    if (selected.length > 0) {
      seeded.current = true;
      return;
    }
    const first = addresses.find((a) => !a.archivedAt);
    if (first) {
      seeded.current = true;
      setValue("addressIds", [first.id], { shouldDirty: false });
    }
  }, [addresses, selected, setValue]);

  const [dialogState, setDialogState] = React.useState<
    { kind: "closed" } | { kind: "create" } | { kind: "edit"; id: string }
  >({ kind: "closed" });

  function toggle(id: string) {
    // A location with booked interviews can't be removed — guarded here and
    // again server-side (address_has_active_interviews).
    if (selected.includes(id) && lockedSet.has(id)) return;
    const next = selected.includes(id)
      ? selected.filter((x) => x !== id)
      : selected.length >= 30
        ? selected
        : [...selected, id];
    setValue("addressIds", next, { shouldDirty: true, shouldValidate: true });
  }

  return (
    <Card className="p-6">
      <SectionHeader
        step={4}
        title="Địa điểm làm việc"
        hint="Chọn từ danh sách cơ sở hoặc thêm mới. Chuỗi nhiều chi nhánh có thể chọn nhiều địa điểm — ứng viên sẽ thấy nhánh gần họ nhất."
      />

      <Controller
        control={control}
        name="addressIds"
        render={({ fieldState }) => (
          <div id="field-addressIds" className="space-y-3">
            {fieldState.error?.message ? (
              <p className="text-xs font-semibold text-danger">
                {fieldState.error.message}
              </p>
            ) : null}

            {loading ? (
              <p className="text-sm text-ink/60">
                Đang tải danh sách địa điểm…
              </p>
            ) : null}

            <div className="flex flex-col gap-2">
              {visible.map((a) => {
                const active = selected.includes(a.id);
                const locked = a.inUseByOpenPostings > 0;
                // Active interviews booked → cannot be removed from this posting.
                const interviewLocked = active && lockedSet.has(a.id);
                const interviewCount = addressInterviewCounts?.[a.id] ?? 0;
                return (
                  <div
                    key={a.id}
                    className={`flex items-center gap-3 rounded-lg border p-3 transition-colors ${
                      active
                        ? "border-ink bg-lime/15"
                        : "border-smoke bg-mist hover:bg-paper"
                    }`}
                  >
                    <Checkbox
                      checked={active}
                      onChange={() => toggle(a.id)}
                      disabled={interviewLocked}
                      aria-label={`Chọn ${a.alias}`}
                      className="shrink-0"
                    />
                    <button
                      type="button"
                      onClick={() => toggle(a.id)}
                      disabled={interviewLocked}
                      className="flex-1 min-w-0 text-left disabled:cursor-not-allowed"
                    >
                      <p className="truncate text-sm font-semibold text-ink">
                        {a.alias}
                        {a.archivedAt ? (
                          <span className="ml-2 text-xs font-normal text-ash">
                            (đã lưu trữ)
                          </span>
                        ) : null}
                      </p>
                      <p className="mt-0.5 truncate text-xs text-ink/65">
                        {[a.address, a.wardName ?? a.district, a.city]
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                      {interviewLocked ? (
                        <p className="mt-1 inline-flex items-center gap-1 rounded-full bg-ember/15 px-2 py-0.5 text-[11px] font-semibold text-ember">
                          <Lock size={11} strokeWidth={2.5} />
                          Đang có {interviewCount} lịch phỏng vấn — không thể bỏ
                        </p>
                      ) : null}
                    </button>
                    <button
                      type="button"
                      onClick={() => setDialogState({ kind: "edit", id: a.id })}
                      disabled={locked}
                      aria-label="Sửa địa điểm"
                      title={
                        locked
                          ? "Không thể sửa khi đang được tin tuyển đang mở dùng"
                          : "Sửa địa điểm"
                      }
                      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-ink/70 transition-colors hover:bg-ink/10 hover:text-ink disabled:cursor-not-allowed disabled:text-ash disabled:hover:bg-transparent"
                    >
                      <Pencil size={14} strokeWidth={2.25} />
                    </button>
                  </div>
                );
              })}
              {!loading && visible.length === 0 ? (
                <p className="text-sm text-ink/60">
                  Chưa có địa điểm nào. Thêm địa điểm đầu tiên để bắt đầu.
                </p>
              ) : null}
            </div>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setDialogState({ kind: "create" })}
            >
              + Thêm địa điểm mới
            </Button>

            <p className="text-xs text-ink/55">
              Đã chọn {selected.length}/30 địa điểm
            </p>
          </div>
        )}
      />

      {dialogState.kind !== "closed" ? (
        <AddressDialog
          AddressPicker={AddressPicker}
          mode={dialogState.kind}
          initial={
            dialogState.kind === "edit"
              ? (addresses.find((x) => x.id === dialogState.id) ?? null)
              : null
          }
          onClose={() => setDialogState({ kind: "closed" })}
          onCreate={async (input) => {
            const next = await onCreate(input);
            // Auto-select the newly created address.
            setValue("addressIds", [...selected, next.id], {
              shouldDirty: true,
              shouldValidate: true,
            });
            setDialogState({ kind: "closed" });
          }}
          onUpdate={async (id, input) => {
            await onUpdate(id, input);
            setDialogState({ kind: "closed" });
          }}
          onArchive={async (id) => {
            await onArchive(id);
            // Deselect if it was in the current set.
            if (selected.includes(id)) {
              setValue(
                "addressIds",
                selected.filter((x) => x !== id),
                { shouldDirty: true, shouldValidate: true },
              );
            }
            setDialogState({ kind: "closed" });
          }}
        />
      ) : null}
    </Card>
  );
}

// Inline create/edit dialog for one OrgAddress. Reuses AddressPicker so the
// employer pastes a Google Maps link to fill street/district/city/GPS.
function AddressDialog(props: {
  AddressPicker: AddressPickerComponent;
  mode: "create" | "edit";
  initial: OrgAddressOption | null;
  onClose: () => void;
  onCreate: (input: OrgAddressInput) => Promise<void>;
  onUpdate: (id: string, input: Partial<OrgAddressInput>) => Promise<void>;
  onArchive: (id: string) => Promise<void>;
}) {
  const {
    AddressPicker,
    mode,
    initial,
    onClose,
    onCreate,
    onUpdate,
    onArchive,
  } = props;
  const [alias, setAlias] = React.useState(initial?.alias ?? "");
  const [picker, setPicker] = React.useState<AddressPickerValue>({
    address: initial?.address ?? "",
    wardCode: initial?.wardCode ?? "",
    wardName: initial?.wardName ?? "",
    wardProvinceCode: initial?.wardProvinceCode ?? "",
    district: initial?.district ?? "",
    city: initial?.city ?? "",
    provinceCode: initial?.provinceCode ?? null,
    provinceName: null,
    lat: initial?.lat ?? null,
    lng: initial?.lng ?? null,
    mapUrl: initial?.mapUrl ?? null,
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const locked = mode === "edit" && (initial?.inUseByOpenPostings ?? 0) > 0;

  async function handleSave() {
    setError(null);
    if (!alias.trim()) {
      setError("Hãy nhập tên gợi nhớ cho địa điểm");
      return;
    }
    if (
      !picker.address ||
      picker.lat === null ||
      picker.lng === null ||
      !picker.wardProvinceCode
    ) {
      setError("Hãy chọn địa chỉ trên Google Maps để có toạ độ GPS");
      return;
    }
    if (!picker.wardCode) {
      setError("Hãy chọn phường / xã");
      return;
    }
    const provinceCode = picker.provinceCode;
    setSubmitting(true);
    try {
      const payload = {
        alias: alias.trim(),
        address: picker.address,
        wardCode: picker.wardCode,
        wardName: picker.wardName,
        wardProvinceCode: picker.wardProvinceCode,
        district: picker.district,
        city: picker.city,
        provinceCode,
        lat: picker.lat,
        lng: picker.lng,
        mapUrl: picker.mapUrl,
      };
      if (mode === "create") {
        await onCreate(payload);
      } else if (initial) {
        await onUpdate(initial.id, payload);
      }
    } catch (e) {
      setError(
        (e as { message?: string } | undefined)?.message ??
          "Có lỗi xảy ra, thử lại sau",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-2xl bg-paper p-6 shadow-xl">
        <div className="flex items-baseline justify-between">
          <h3 className="text-lg font-extrabold text-ink">
            {mode === "create" ? "Thêm địa điểm mới" : "Sửa địa điểm"}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="text-sm font-semibold text-ink/60 hover:text-ink"
          >
            Đóng
          </button>
        </div>

        {locked ? (
          <p className="mt-3 rounded-md bg-ember/15 px-3 py-2 text-xs font-semibold text-ember">
            Địa điểm này đang được {initial?.inUseByOpenPostings} tin tuyển dụng
            sử dụng — không thể chỉnh sửa. Đóng tin trước, hoặc bỏ chọn địa điểm
            khỏi tin đó.
          </p>
        ) : null}

        <div className="mt-4 space-y-4">
          <FormField label="Tên gợi nhớ">
            <Input
              value={alias}
              onChange={(e) => setAlias(e.target.value)}
              placeholder="VD: Chi nhánh Tôn Đức Thắng"
              maxLength={60}
              disabled={locked}
            />
          </FormField>

          <AddressPicker
            label="Địa chỉ + GPS"
            value={picker}
            onChange={setPicker}
            disabled={locked}
          />

          {error ? (
            <p className="text-xs font-semibold text-danger">{error}</p>
          ) : null}
        </div>

        <div className="mt-6 flex items-center justify-between gap-2">
          {mode === "edit" && initial ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={locked || submitting}
              onClick={async () => {
                if (!confirm(`Lưu trữ "${initial.alias}"?`)) return;
                setSubmitting(true);
                try {
                  await onArchive(initial.id);
                } catch (e) {
                  setError(
                    (e as { message?: string } | undefined)?.message ??
                      "Lưu trữ thất bại",
                  );
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              Lưu trữ
            </Button>
          ) : (
            <div />
          )}
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onClose}
              disabled={submitting}
            >
              Huỷ
            </Button>
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={handleSave}
              loading={submitting}
              disabled={locked}
            >
              {mode === "create" ? "Thêm" : "Lưu"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Advanced demographics (collapsed) ──────────────────────────────────────
export function AdvancedSection() {
  const { control, register, formState } = useCtx();
  const [open, setOpen] = React.useState(false);

  return (
    <Card className="p-0 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between gap-3 px-6 py-4 text-left"
      >
        <div>
          <h3 className="text-base font-extrabold text-ink">
            Yêu cầu nâng cao
          </h3>
          <p className="mt-0.5 text-xs text-ink/60">
            Tuỳ chọn — mặc định nhận từ {DEFAULT_MIN_LABOR_AGE} tuổi.
          </p>
        </div>
        <span className="text-lg text-ink/60">{open ? "▾" : "▸"}</span>
      </button>

      {open ? (
        <div className="space-y-4 border-t border-smoke px-6 pb-6 pt-4">
          <p className="text-xs text-ink/60">
            Để trống = không lọc theo tiêu chí đó. Filter quá hẹp có thể khiến
            ít ứng viên match.
          </p>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <FormField label="Giới tính">
              <Controller
                control={control}
                name="reqGender"
                render={({ field }) => (
                  <Select
                    value={
                      field.value === null || field.value === undefined
                        ? GENDER_ANY
                        : String(field.value)
                    }
                    onValueChange={(v) =>
                      field.onChange(
                        v === GENDER_ANY
                          ? null
                          : (v as Exclude<Gender, "OTHER">),
                      )
                    }
                    options={GENDER_OPTIONS}
                  />
                )}
              />
            </FormField>

            <FormField
              label="Chiều cao tối thiểu (cm)"
              error={formState.errors.reqMinHeightCm?.message}
            >
              <Controller
                control={control}
                name="reqMinHeightCm"
                render={({ field }) => (
                  <Input
                    type="number"
                    min={120}
                    max={220}
                    value={
                      field.value === null || field.value === undefined
                        ? ""
                        : String(field.value)
                    }
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                    placeholder="—"
                  />
                )}
              />
            </FormField>

            <FormField
              label={`Tuổi tối thiểu (mặc định ${DEFAULT_MIN_LABOR_AGE})`}
              error={formState.errors.reqMinAge?.message}
            >
              <Controller
                control={control}
                name="reqMinAge"
                render={({ field }) => (
                  <Input
                    type="number"
                    min={14}
                    max={80}
                    value={
                      field.value === null || field.value === undefined
                        ? ""
                        : String(field.value)
                    }
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                    placeholder={String(DEFAULT_MIN_LABOR_AGE)}
                  />
                )}
              />
            </FormField>

            <FormField
              label="Tuổi tối đa"
              error={formState.errors.reqMaxAge?.message}
            >
              <Controller
                control={control}
                name="reqMaxAge"
                render={({ field }) => (
                  <Input
                    type="number"
                    min={14}
                    max={80}
                    value={
                      field.value === null || field.value === undefined
                        ? ""
                        : String(field.value)
                    }
                    onChange={(e) =>
                      field.onChange(
                        e.target.value === "" ? null : Number(e.target.value),
                      )
                    }
                    placeholder="—"
                  />
                )}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-1 gap-3 text-sm sm:grid-cols-3">
            <Controller
              control={control}
              name="reqMotorbike"
              render={({ field }) => (
                <Checkbox
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  label="Cần có xe máy"
                />
              )}
            />
            <Controller
              control={control}
              name="reqExcludeTattoo"
              render={({ field }) => (
                <Checkbox
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  label="Không có hình xăm lộ ra ngoài"
                />
              )}
            />
            <Controller
              control={control}
              name="reqKyc"
              render={({ field }) => (
                <Checkbox
                  checked={field.value}
                  onChange={(e) => field.onChange(e.target.checked)}
                  label="Cần xác thực CCCD"
                />
              )}
            />
          </div>

          <FormField
            label="Ghi chú khác"
            error={formState.errors.softNotes?.message}
          >
            <Textarea
              {...register("softNotes")}
              rows={2}
              maxLength={500}
              placeholder="VD: Hoà đồng, cẩn thận, làm việc nhóm tốt"
            />
          </FormField>
        </div>
      ) : null}
    </Card>
  );
}

// Admin-only: provenance link (e.g. Facebook permalink). Self-gates on the
// form mode's `showSourceUrl` flag, so it renders nothing in employer forms.
export function SourceUrlSection() {
  const { control } = useCtx();
  const { showSourceUrl } = usePostingFormMode();
  if (!showSourceUrl) return null;

  return (
    <Card className="p-6">
      <h3 className="text-base font-extrabold text-ink">
        Link nguồn (Facebook)
      </h3>
      <p className="mt-0.5 text-xs text-ink/60">
        Chỉ admin thấy. Dán link bài đăng Facebook gốc nếu có.
      </p>
      <div className="mt-4">
        <Controller
          control={control}
          name="sourceUrl"
          render={({ field }) => (
            <Input
              type="url"
              value={field.value ?? ""}
              onChange={(e) =>
                field.onChange(e.target.value === "" ? null : e.target.value)
              }
              placeholder="https://facebook.com/..."
            />
          )}
        />
      </div>
    </Card>
  );
}

export { DEFAULTS };
